import { z } from 'zod';

// NOTIFY payload schemas for the playtests epic.
//
// `recruitment_post_request` carries a `trigger` discriminator so the bot
// listener can decide whether to advance `last_posted_at` (first post on
// enable, or manual "Repost now") or `last_reposted_at` (cadence sweep).

export const RECRUITMENT_POST_TRIGGERS = ['enabled', 'manual', 'cadence'] as const;
export const RecruitmentPostTriggerSchema = z.enum(RECRUITMENT_POST_TRIGGERS);
export type RecruitmentPostTrigger = z.infer<typeof RecruitmentPostTriggerSchema>;

export const RecruitmentPostRequestPayloadSchema = z.object({
  gameId: z.string().uuid(),
  trigger: RecruitmentPostTriggerSchema,
});
export type RecruitmentPostRequestPayload = z.infer<
  typeof RecruitmentPostRequestPayloadSchema
>;

// Emitted by the playtest_recruitment_configs trigger when the studio
// flips enabled true -> false. The bot deletes the previously-posted
// recruitment messages from each configured channel so the button stops
// working immediately. Snapshot of posted_message_ids comes in the
// payload so the listener does not need a second DB read.
export const RecruitmentDisableRequestPayloadSchema = z.object({
  gameId: z.string().uuid(),
  postedMessageIds: z.record(z.string()),
});
export type RecruitmentDisableRequestPayload = z.infer<
  typeof RecruitmentDisableRequestPayloadSchema
>;

// Emitted by the playtest_dm_dispatch_notify trigger (0049 / 0063) on
// every insert into playtest_dm_dispatch. The bot's playtest dispatch
// listener filters on `kind === 'playtest'` to ignore the legacy
// template-based payloads emitted on dm_dispatch_queue inserts (which
// will gain a different `kind` field of their own when that path is
// migrated).
export const PlaytestDmDispatchPayloadSchema = z.object({
  kind: z.literal('playtest'),
  dispatchId: z.string().uuid(),
  playtestSessionId: z.string().uuid(),
  eventLogId: z.string().uuid().nullable(),
  discordUserId: z.string().min(1),
  createdAt: z.string().datetime({ offset: true }),
});
export type PlaytestDmDispatchPayload = z.infer<
  typeof PlaytestDmDispatchPayloadSchema
>;

// Emitted by the enqueue_playtest_form_sentiment trigger (0107) when a
// playtest_dm_dispatch row transitions to status='responded' (one per
// playtest form submission). The bot's playtest-form sentiment worker
// treats it as a wake-up to drain the playtest_form_sentiment queue. The
// ids are carried for log context; the worker selects the next pending row
// itself rather than acting on the payload directly.
export const PlaytestFormSentimentQueuedPayloadSchema = z.object({
  sessionId: z.string().uuid(),
  eventId: z.string().uuid(),
  studioId: z.string().uuid(),
  gameId: z.string().uuid(),
});
export type PlaytestFormSentimentQueuedPayload = z.infer<
  typeof PlaytestFormSentimentQueuedPayloadSchema
>;
